import { invoke, isTauri } from '@tauri-apps/api/core';
import { api } from '@/api/client';

type MobilePlatform = 'android' | 'ios';

const DEVICE_ID_KEY = 'pro-kanban.device-id';
const TOKEN_KEY = 'pro-kanban.fcm-token';
let listening = false;

export async function enableMobilePush(): Promise<boolean> {
  const platform = await mobilePlatform();
  if (!platform) return false;

  const fcm = await import('tauri-plugin-fcm');
  if (!listening) {
    await fcm.onTokenRefresh(({ token }) => {
      void registerToken(token, platform).catch((error) => console.warn('Could not refresh the FCM token', error));
    });
    listening = true;
  }

  let permission = await fcm.checkPermissions();
  if (permission === 'prompt' || permission === 'prompt-with-rationale') permission = await fcm.requestPermissions();
  if (permission !== 'granted') return false;

  await fcm.register();
  const { token } = await fcm.getToken();
  await registerToken(token, platform);
  return true;
}

export async function unregisterMobilePush(): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;

  try {
    await api.delete('users/me/device-tokens', { token });
  } finally {
    localStorage.removeItem(TOKEN_KEY);
    if (await mobilePlatform()) {
      const { deleteToken } = await import('tauri-plugin-fcm');
      await deleteToken().catch(() => undefined);
    }
  }
}

async function registerToken(token: string, platform: MobilePlatform): Promise<void> {
  const { getVersion } = await import('@tauri-apps/api/app');
  await api.post('users/me/device-tokens', {
    token,
    platform,
    deviceId: installationId(),
    appVersion: await getVersion(),
  });
  localStorage.setItem(TOKEN_KEY, token);
}

async function mobilePlatform(): Promise<MobilePlatform | null> {
  if (!isTauri()) return null;
  const platform = await invoke<string>('runtime_platform');
  return platform === 'android' || platform === 'ios' ? platform : null;
}

function installationId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, id);
  return id;
}
